
import { useState, useCallback } from "react";
import userService from "../services/userService";
import roleService from "../services/roleService"; 

export default function useEmployee() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);


  const formatEmployee = (u) => {
    if (!u) return null;

    return {
      id: u.id,
      name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—",
      email: u.email || "—",
      phone: u.phoneNumber || "—",
      role: u.roles?.[0]?.name || "—",
      reportsTo: u.updatedBy || "—",
      raw: u,
    };
  };


  // GET ALL EMPLOYEES

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);

      const response = await userService.getAllUsers();
      const data = Array.isArray(response.data) ? response.data : response;

      const formatted = data.map((item) => formatEmployee(item));
      setEmployees(formatted);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  
  // GET EMPLOYEE BY ID
  
  const fetchEmployeeById = async (id) => {
    try {
      const res = await userService.getUserById(id);
      return res.data || res;
    } catch (err) {
      console.error(`Failed to fetch employee ${id}:`, err);
      throw err;
    }
  };

  
  // CREATE EMPLOYEE
  
  const createEmployee = async (payload) => {
    try {
      const res = await userService.createUser(payload);
      const created = res.data || res;

      const formatted = formatEmployee(created);
      setEmployees((prev) => [...prev, formatted]);

      return formatted;
    } catch (err) {
      console.error("Failed to create employee:", err);
      throw err;
    }
  };

  
  // UPDATE EMPLOYEE
  
  const updateEmployee = async (id, payload) => {
    try {
      const res = await userService.updateUser(id, payload);
      const updated = res.data || res;

      const formatted = formatEmployee(updated);

      setEmployees((prev) =>
        prev.map((emp) => (emp.id === id ? formatted : emp))
      );

      return formatted;
    } catch (err) {
      console.error(`Failed to update employee ${id}:`, err);
      throw err;
    }
  };


  // DELETE EMPLOYEE

  const deleteEmployee = async (id) => {
    try {
      await userService.deleteUser(id);
      setEmployees((prev) => prev.filter((emp) => emp.id !== id));
      return true;
    } catch (err) {
      console.error(`Failed to delete employee ${id}:`, err);
      throw err;
    }
  };


  //  GET ALL ROLES (excludes internal roles)

  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);

      const response = await roleService.getAllRoles();
      const data = Array.isArray(response.data) ? response.data : response;

      // Filter out internal roles (e.g., system roles not assignable to employees)
      const formatted = data
        .filter((role) => !role.internalRole)
        .map((role) => ({
          id: role.id,
          name: role.name,
          internal: role.internalRole,
          permissions: role.permissions || [],
          raw: role,
        }));

      setRoles(formatted);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    } finally {
      setRolesLoading(false);
    }
  }, []);


  return {
    employees,
    loading,
    fetchEmployees,
    fetchEmployeeById,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    roles,
    rolesLoading,
    fetchRoles,
  };
}
